import { useRef } from 'react';
import { useMergeState } from '@/shared/lib/customHooks';
import { computeDragBounds } from './handler';

/**
 * Drives the draggable modal's disabled/bounds state and the ref used to measure it. Enabling dragging only while the pointer is over the drag zone, and recomputing bounds on drag start so the modal can never be dragged outside the viewport.
 *
 * @returns state and setState (disabled/bounds), draggleRef to attach to the wrapper, and onStart to pass to Draggable.
 */
const useDraggableModalWrapper = () => {
  const [state, setState] = useMergeState({
    disabled: true,
    bounds: {
      left: 0,
      top: 0,
      bottom: 0,
      right: 0,
    },
  });
  const draggleRef = useRef<HTMLDivElement>(null);

  /**
   * Recomputes the drag bounds for the modal so it can never be dragged outside the viewport. Measures the wrapper's bounding rect, bails out when the ref is not yet mounted, and stores left, right, top and bottom offsets derived from the document element's client size and the current drag delta.
   *
   * @param event - The drag event raised by the draggable wrapper.
   * @param uiData - Drag position data supplying the current x and y offsets.
   * @returns Nothing.
   */
  const onStart = (event: any, uiData: any) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) {
      return;
    }
    setState({
      bounds: computeDragBounds(targetRect, uiData, clientWidth, clientHeight),
    });
  };

  return { state, setState, draggleRef, onStart };
};

export default useDraggableModalWrapper;
